// Admin API Service
// Implements endpoints from /api/v1/admin OpenAPI specification

import api from '../../lib/api';
import type { AxiosResponse } from '../../lib/api';
import type {
  DashboardMetrics,
  UsageTrend,
  ModelUsage,
  SystemHealth,
  SystemResources,
  LLMProcess,
  Activity,
  UserDetail,
  UserListResponse,
  UserListParams,
  UserStats,
  UserCreate,
  UserUpdate,
  PasswordResetResponse,
  ConversationListResponse,
  MessageListResponse,
  LLMProvider,
  LLMProviderCreate,
  LLMProviderUpdate,
  LLMModel,
  LLMModelUpdate,
  ProviderTestResult,
  ApiKeyRotateResponse,
} from '../../types/admin';

// Dashboard Endpoints
export const dashboardAPI = {
  getMetrics: (): Promise<AxiosResponse<DashboardMetrics>> =>
    api.get<DashboardMetrics>('/api/v1/admin/dashboard/metrics'),

  getUsageTrends: (
    period: '7d' | '30d' | '90d' = '7d'
  ): Promise<AxiosResponse<{ trends: UsageTrend[] }>> =>
    api.get<{ trends: UsageTrend[] }>('/api/v1/admin/dashboard/usage-trends', {
      params: { period },
    }),

  getModelUsage: (): Promise<AxiosResponse<ModelUsage[]>> =>
    api.get<ModelUsage[]>('/api/v1/admin/dashboard/model-usage'),
};

// System Endpoints
export const systemAPI = {
  getHealth: (): Promise<AxiosResponse<SystemHealth>> =>
    api.get<SystemHealth>('/api/v1/admin/system/health'),

  getResources: (): Promise<AxiosResponse<SystemResources>> =>
    api.get<SystemResources>('/api/v1/admin/system/resources'),

  getProcesses: (): Promise<AxiosResponse<LLMProcess[]>> =>
    api.get<LLMProcess[]>('/api/v1/admin/system/processes'),

  stopProcess: (pid: number): Promise<AxiosResponse<void>> =>
    api.post<void>(`/api/v1/admin/system/processes/${pid}/stop`),
};

// Activity Endpoints
export const activityAPI = {
  getRecent: (limit: number = 20): Promise<AxiosResponse<Activity[]>> =>
    api.get<Activity[]>('/api/v1/admin/activity/recent', {
      params: { limit },
    }),
};

// User Management Endpoints
export const userManagementAPI = {
  list: (params: UserListParams = {}): Promise<AxiosResponse<UserListResponse>> =>
    api.get<UserListResponse>('/api/v1/admin/users', { params }),

  get: (userId: number): Promise<AxiosResponse<UserDetail>> =>
    api.get<UserDetail>(`/api/v1/admin/users/${userId}`),

  create: (userData: UserCreate): Promise<AxiosResponse<UserDetail>> =>
    api.post<UserDetail>('/api/v1/admin/users', userData),

  update: (userId: number, userData: UserUpdate): Promise<AxiosResponse<UserDetail>> =>
    api.put<UserDetail>(`/api/v1/admin/users/${userId}`, userData),

  delete: (userId: number): Promise<AxiosResponse<void>> =>
    api.delete<void>(`/api/v1/admin/users/${userId}`),

  activate: (userId: number): Promise<AxiosResponse<UserDetail>> =>
    api.post<UserDetail>(`/api/v1/admin/users/${userId}/activate`),

  deactivate: (userId: number): Promise<AxiosResponse<UserDetail>> =>
    api.post<UserDetail>(`/api/v1/admin/users/${userId}/deactivate`),

  resetPassword: (userId: number): Promise<AxiosResponse<PasswordResetResponse>> =>
    api.post<PasswordResetResponse>(`/api/v1/admin/users/${userId}/reset-password`),

  getStats: (): Promise<AxiosResponse<UserStats>> =>
    api.get<UserStats>('/api/v1/admin/users/stats'),

  // Conversation and Message Endpoints
  getUserConversations: (
    userId: number,
    page: number = 1,
    pageSize: number = 20
  ): Promise<AxiosResponse<ConversationListResponse>> =>
    api.get<ConversationListResponse>(`/api/v1/admin/users/${userId}/chats`, {
      params: { page, page_size: pageSize },
    }),

  getConversationMessages: (
    conversationId: number,
    page: number = 1,
    pageSize: number = 50
  ): Promise<AxiosResponse<MessageListResponse>> =>
    api.get<MessageListResponse>(`/api/v1/admin/chats/${conversationId}/messages`, {
      params: { page, page_size: pageSize },
    }),
};

// LLM Provider Endpoints
export const llmProviderAPI = {
  list: (): Promise<AxiosResponse<LLMProvider[]>> =>
    api.get<LLMProvider[]>('/api/v1/admin/llm/providers'),

  get: (providerId: string): Promise<AxiosResponse<LLMProvider>> =>
    api.get<LLMProvider>(`/api/v1/admin/llm/providers/${providerId}`),

  create: (providerData: LLMProviderCreate): Promise<AxiosResponse<LLMProvider>> =>
    api.post<LLMProvider>('/api/v1/admin/llm/providers', providerData),

  update: (
    providerId: string,
    providerData: LLMProviderUpdate
  ): Promise<AxiosResponse<LLMProvider>> =>
    api.put<LLMProvider>(`/api/v1/admin/llm/providers/${providerId}`, providerData),

  delete: (providerId: string): Promise<AxiosResponse<void>> =>
    api.delete<void>(`/api/v1/admin/llm/providers/${providerId}`),

  rotateKey: (
    providerId: string,
    data: { new_api_key: string }
  ): Promise<AxiosResponse<ApiKeyRotateResponse>> =>
    api.post<ApiKeyRotateResponse>(`/api/v1/admin/llm/providers/${providerId}/rotate-key`, data),

  test: (providerId: string): Promise<AxiosResponse<ProviderTestResult>> =>
    api.post<ProviderTestResult>(`/api/v1/admin/llm/providers/${providerId}/test`),

  // Model Endpoints
  listModels: (
    providerId?: string,
    enabledOnly: boolean = false
  ): Promise<AxiosResponse<LLMModel[]>> =>
    api.get<LLMModel[]>('/api/v1/admin/llm/models', {
      params: { provider_id: providerId, enabled_only: enabledOnly },
    }),

  updateModel: (modelId: string, modelData: LLMModelUpdate): Promise<AxiosResponse<LLMModel>> =>
    api.put<LLMModel>(`/api/v1/admin/llm/models/${modelId}`, modelData),
};

// Export all APIs as a single object for convenience
export const adminAPI = {
  dashboard: dashboardAPI,
  system: systemAPI,
  activity: activityAPI,
  users: userManagementAPI,
  llm: llmProviderAPI,
};
